<template>
  <div class="banner-carousel">
    <div ref="swiperEl" class="swiper">
      <div class="swiper-wrapper">
        <div v-for="banner in normalizedBanners" :key="banner.id" class="swiper-slide">
          <button
            v-if="banner.modalTarget"
            class="banner-carousel__button"
            type="button"
            data-bs-toggle="modal"
            :data-bs-target="banner.modalTarget"
            :aria-label="banner.ariaLabel ?? `${banner.alt}を開く`"
          >
            <img
              :src="banner.imageUrl"
              :alt="banner.alt"
              class="banner-carousel__image"
              loading="lazy"
            />
          </button>
          <a
            v-else
            class="banner-carousel__link"
            :href="banner.linkUrl"
            :target="banner.linkTarget"
            :rel="banner.linkRel"
          >
            <img
              :src="banner.imageUrl"
              :alt="banner.alt"
              class="banner-carousel__image"
              loading="lazy"
            />
          </a>
        </div>
      </div>
      <div ref="paginationEl" class="banner-carousel__pagination"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import 'swiper/css';
import 'swiper/css/pagination';
import Swiper from 'swiper';
import { Autoplay, Pagination } from 'swiper/modules';
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

import { DEFAULT_AUTOPLAY_DELAY_MS, defaultBanners, resolveFeaturedBanner } from './bannerItems';
import type { BannerConfig } from './types';

const swiperEl = ref<HTMLElement | null>(null);
const paginationEl = ref<HTMLElement | null>(null);
const swiperInstance = ref<Swiper | null>(null);

const featuredBanner = resolveFeaturedBanner();

const normalizeBanner = (banner: BannerConfig): BannerConfig => {
  const modalTarget = banner.modalTarget
    ? banner.modalTarget.startsWith('#')
      ? banner.modalTarget
      : `#${banner.modalTarget}`
    : undefined;

  const isExternal = banner.linkUrl?.startsWith('http');
  const linkTarget = banner.linkTarget ?? (isExternal ? '_blank' : '_self');

  return {
    ...banner,
    modalTarget,
    linkTarget,
    linkRel: banner.linkRel ?? (linkTarget === '_blank' ? 'noopener noreferrer' : undefined),
  };
};

const normalizedBanners = computed<BannerConfig[]>(() => {
  const allBanners = featuredBanner ? [featuredBanner, ...defaultBanners] : [...defaultBanners];
  return allBanners.map((banner) => normalizeBanner(banner));
});

const debounce = (fn: (...args: unknown[]) => void, delay: number) => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  return (...args: unknown[]) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

const initSwiper = () => {
  if (!swiperEl.value || !paginationEl.value) return;

  if (swiperInstance.value) {
    swiperInstance.value.destroy(true, true);
  }

  const shouldLoop = normalizedBanners.value.length > 1;

  swiperInstance.value = new Swiper(swiperEl.value, {
    modules: [Autoplay, Pagination],
    slidesPerView: 1,
    spaceBetween: 12,
    speed: 600,
    loop: shouldLoop,
    grabCursor: true,
    pagination: {
      el: paginationEl.value,
      clickable: true,
    },
    autoplay: shouldLoop
      ? {
          delay: DEFAULT_AUTOPLAY_DELAY_MS,
          disableOnInteraction: false,
        }
      : false,
    breakpoints: {
      640: {
        slidesPerView: 2,
        slidesPerGroup: 1,
      },
      1000: {
        slidesPerView: 3,
        slidesPerGroup: 1,
      },
    },
    observer: true,
    observeParents: true,
  });
};

const debouncedInit = debounce(() => initSwiper(), 250);

onMounted(async () => {
  await nextTick();
  initSwiper();
  window.addEventListener('resize', debouncedInit);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', debouncedInit);
  if (swiperInstance.value) {
    swiperInstance.value.destroy(true, true);
    swiperInstance.value = null;
  }
});
</script>

<style scoped lang="scss">
.banner-carousel {
  width: 100%;
}

.swiper {
  width: 100%;
  position: relative;
  overflow: hidden;
}

.swiper-slide {
  height: auto;
  display: flex;
}

.banner-carousel__link,
.banner-carousel__button {
  display: block;
  width: 100%;
  height: 100%;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
}

.banner-carousel__image {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
  aspect-ratio: 16 / 8;
}

.banner-carousel__pagination {
  margin-top: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
}

:deep(.swiper-pagination-bullet) {
  width: 8px;
  height: 8px;
  background: #e0e0e0;
  opacity: 1;
}

:deep(.swiper-pagination-bullet-active) {
  background: #0090c2;
}
</style>
